import { EventDataTrack, ITrackNew, Main, Provider, systemNames } from '@user-portal/wikr-analytics';
import { ERROR_LEVELS, SENTRY_ANALYTIC } from '@user-portal/sentry-utils';

import { getAwsId } from 'api/utils/awsIdManagement';

import sentry from 'services/Sentry/SentryInstance';

import { CustomDataOptions, ITrackOptions, TrackOptionsScreenLoad } from './types';

export const initAmazonAnalytics = () => {
    Main.initSystems({
        activeSystems: [
            {
                name: systemNames.amazon,
                id: import.meta.env.AMAZON_ID,
                config: {
                    releaseDate: import.meta.env.RELEASE_DATE,
                    streamName: import.meta.env.AWS_DELIVERY_STREAM_NAME,
                    envMode: import.meta.env.ENV,
                    awsId: getAwsId(),
                },
            },
        ],
    });
};

const clientHandlerFn = (e: Error | unknown) => sentry.logError(e as Error, SENTRY_ANALYTIC, ERROR_LEVELS.ERROR);

export const setUserDataToAnalytics = ({
    country,
    id,
    gender,
    abTestName,
    growthBookTestName,
    region,
}: {
    id: string;
    country: string;
    gender: string;
    abTestName: string;
    growthBookTestName?: string;
    region: string | null;
}) => {
    Provider.provideData({
        gender,
        // MB started to work with a string ids
        // @ts-ignore
        userId: id,
        country: async () => country,
        abTestName,
        clientHandlerFn,
        growthBookTestName,
        region,
    });
};

export const clearUserDataFromAnalytics = () => {
    Provider.provideData({
        userId: undefined,
        gender: '',
        country: async () => null,
        abTestName: '',
        clientHandlerFn,
        growthBookTestName: '',
        region: null,
    });
};

// old version, new one -> trackNewScreenLoad
export const trackScreenLoad = (eventId: string, options?: TrackOptionsScreenLoad) => {
    Main.track(eventId, options);
};

export const trackDecoratorWithExtendedLabel = (
    eventType: string,
    eventAction?: string,
    eventLabel?: object,
    customFields?: object
) => {
    const trackParams: ITrackNew = {
        eventData: eventType,
        actionData: eventAction,
    };

    if (eventLabel) {
        trackParams.data = { event_label: eventLabel };
    }

    if (customFields) {
        trackParams.data = { ...trackParams.data, ...customFields };
    }

    Main.trackNew(trackParams);
};

export const basicTrackDecorator = (
    eventType: string,
    eventAction?: string,
    eventLabel?: string | number,
    customFields?: object
) => {
    const trackParams: ITrackNew = {
        eventData: eventType,
        actionData: eventAction,
    };

    if (eventLabel) {
        trackParams.data = { event_label: `${eventLabel}` };
    }

    if (customFields) {
        trackParams.data = { ...trackParams.data, ...customFields };
    }

    Main.trackNew(trackParams);
};

export const customTrackDecorator = (eventType: string, options: ITrackOptions) => {
    const trackParams: ITrackNew = {
        eventData: eventType,
        actionData: options?.event_action,
    };

    const data: EventDataTrack = {};

    if (options?.event_label) {
        data.event_label = options.event_label;
    }

    if (options?.contentId) {
        data.content_id = options.contentId;
    }

    trackParams.data = data;

    Main.trackNew(trackParams);
};

export const trackClick = (eventId: string, options: CustomDataOptions = {}) => {
    customTrackDecorator(`${eventId}__click`, options);
};

export const trackEvent = (eventId: string, options: CustomDataOptions = {}) => {
    customTrackDecorator(eventId, options);
};

export const trackNewScreenLoad = (
    eventId: string,
    { event_label, event_action, contentId }: CustomDataOptions = {}
) => {
    customTrackDecorator(`${eventId}__screen__load`, {
        event_action: event_action ? event_action : 'screen__load',
        event_label: event_label,
        contentId: contentId,
    });
};
